import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { ChevronRight, Save, X, RotateCcw, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import { sportTypeAPI, academyAPI } from '../../services/api';
import AcademyBasicInfoForm, {
  EMPTY_ACADEMY_FORM, academyToFormData, buildAcademyPayload, validateAcademyForm, mergePendingAdditions,
} from '../components/AcademyBasicInfoForm';

const toSlug = (name) =>
  name.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const CreateEditAcademyPage = () => {
  const { academyId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isEditMode = !!academyId;

  const [formData, setFormData] = useState(EMPTY_ACADEMY_FORM);
  const [selectedAddressDetails, setSelectedAddressDetails] = useState(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [sportTypes, setSportTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingAcademy, setLoadingAcademy] = useState(isEditMode);
  const [userRole, setUserRole] = useState(null);
  const basicFormRef = useRef(null);

  useEffect(() => {
    sportTypeAPI.getAll()
      .then(res => setSportTypes(res?.data || res || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    academyAPI.getById(academyId)
      .then(academy => {
        setUserRole(academy.userRole || null);
        setFormData(academyToFormData(academy));
        setSlugManuallyEdited(true);
      })
      .catch(() => toast.error('Failed to load academy'))
      .finally(() => setLoadingAcademy(false));
  }, [isEditMode, academyId]);

  // Single generic field setter — the same shape AcademySettingsTab's setBasic
  // uses, so AcademyBasicInfoForm's field handlers work unmodified in both.
  const handleFieldChange = (key, value) => {
    if (key === 'name' && !slugManuallyEdited) {
      setFormData(prev => ({ ...prev, name: value, slug: toSlug(value) }));
    } else if (key === 'slug') {
      setSlugManuallyEdited(true);
      setFormData(prev => ({ ...prev, slug: value }));
    } else {
      setFormData(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleHoursChange = (type, field, value) => {
    setFormData(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
  };

  const resetSlug = () => {
    setSlugManuallyEdited(false);
    setFormData(prev => ({ ...prev, slug: toSlug(prev.name) }));
  };

  const handleAddressSelect = (addressData) => {
    handleFieldChange('address', addressData.fullAddress || '');
    setSelectedAddressDetails(addressData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const effectiveFormData = mergePendingAdditions(formData, basicFormRef.current?.getPendingAdditions());
    const error = validateAcademyForm(effectiveFormData);
    if (error) return toast.error(error);

    setSaving(true);
    try {
      const payload = buildAcademyPayload(effectiveFormData, selectedAddressDetails);

      if (isEditMode) {
        await academyAPI.update(academyId, payload);
        toast.success('Academy updated successfully!');
      } else {
        const academy = await academyAPI.create(payload);
        toast.success('Academy created successfully!');
        navigate(`/academy/${academy.slug}?tab=settings`);
        return;
      }
      navigate('/academies');
    } catch (err) {
      toast.error(err?.message || 'Failed to save academy');
    } finally {
      setSaving(false);
    }
  };

  if (loadingAcademy) {
    return (
      <div className={`${theme.background} min-h-screen flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  const canEdit = !isEditMode || userRole === 'owner' || userRole === 'admin';

  if (!canEdit) {
    return (
      <div className={`${theme.background} min-h-screen flex items-center justify-center px-4`}>
        <div className={`${theme.card} p-8 rounded-xl max-w-md text-center`}>
          <ShieldAlert size={40} className="mx-auto mb-4 text-red-500" />
          <h1 className={`text-xl font-bold ${theme.text} mb-2`}>Permission Denied</h1>
          <p className={`${theme.textSecondary} mb-6`}>
            You do not have permission to edit this academy.
          </p>
          <Link
            to={`/academies/${academyId}`}
            className={`inline-flex items-center px-4 py-2 ${theme.buttonPrimary} rounded-lg`}
          >
            Back to Academy
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`${theme.background} min-h-screen pb-12`}>
      {/* Breadcrumb */}
      <div className={`${theme.card} border-b`}>
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-x-2 text-sm">
            <Link to="/" className={`${theme.link} hover:underline`}>Home</Link>
            <ChevronRight size={16} className={theme.textSecondary} />
            <Link to="/academies" className={`${theme.link} hover:underline`}>Academies</Link>
            <ChevronRight size={16} className={theme.textSecondary} />
            <span className={theme.text}>{isEditMode ? 'Edit Academy' : 'New Academy'}</span>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className={`text-3xl font-bold ${theme.text}`}>
              {isEditMode ? 'Edit Academy' : 'Create New Academy'}
            </h1>
            <p className={theme.textSecondary}>
              {isEditMode ? 'Update academy information' : 'Set up your sports academy'}
            </p>
          </div>
          <button
            onClick={() => navigate('/academies')}
            className={`flex items-center gap-x-2 px-4 py-2 border ${theme.border} ${theme.text} rounded-lg hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white transition`}
          >
            <X size={18} />
            <span>Cancel</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <AcademyBasicInfoForm
            ref={basicFormRef}
            formData={formData}
            onChange={handleFieldChange}
            onHoursChange={handleHoursChange}
            onAddressSelect={handleAddressSelect}
            sportTypes={sportTypes}
            theme={theme}
            slugExtra={slugManuallyEdited && (
              <button type="button" onClick={resetSlug} className={`flex items-center gap-1 text-xs ${theme.textMuted} hover:text-blue-500 transition`}>
                <RotateCcw size={11} /> Sync with name
              </button>
            )}
          />

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate('/academies')}
              className={`px-6 py-2 border ${theme.border} ${theme.text} rounded-lg hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white transition`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`flex items-center gap-2 px-6 py-2 ${theme.buttonPrimary} rounded-lg disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <Save size={18} />
              {saving ? 'Saving…' : isEditMode ? 'Update Academy' : 'Create Academy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEditAcademyPage;
